import { useState, useMemo, useCallback } from 'react';
import { toast } from '../../../../../../../ui/toast';
import { searchPreScreenQuestions } from '../../../../../../../utils/api_service/pre_screen_api';
import { debounce } from '../../../../../../../utils/helpers/debounce';
import { PreScreenQuestionBank } from '../../../../../../../utils/types/assessment_types';
import { IBankQuestionOption } from '../interfaces';
import { formatBankQuestionsToOptions } from '../functions';
import { QUESTION_SEARCH_DEBOUNCE_MS, DEFAULT_PAGE_SIZE } from '../constants';

export const useQuestionBankSearch = () => {
  const [bankQuestions, setBankQuestions] = useState<PreScreenQuestionBank[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const fetchOptions = useCallback(
    async (inputValue: string): Promise<IBankQuestionOption[]> => {
      setIsSearching(true);
      try {
        const response = await searchPreScreenQuestions(
          inputValue,
          null,
          1,
          DEFAULT_PAGE_SIZE,
        );
        if (response.ok) {
          const data = await response.json();
          const loadedQuestions: PreScreenQuestionBank[] = data.questions || [];
          setBankQuestions(loadedQuestions);
          return formatBankQuestionsToOptions(loadedQuestions);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error, { position: 'bottom-center' });
          return [];
        }
      } catch (error) {
        toast.error(`Error fetching questions: ${error}`, {
          position: 'bottom-center',
        });
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [],
  );

  const loadOptionsDebounced = useMemo(
    () =>
      debounce(
        async (
          inputValue: string,
          callback: (options: IBankQuestionOption[]) => void,
        ) => {
          const options = await fetchOptions(inputValue);
          callback(options);
        },
        QUESTION_SEARCH_DEBOUNCE_MS,
      ),
    [fetchOptions],
  );

  return {
    bankQuestions,
    isSearching,
    fetchOptions,
    loadOptionsDebounced,
  };
};
